import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { reaction } from 'mobx';
import { useIntl } from 'react-intl';
import { useMessagingStore } from '../hooks/useMessagingStore.tsx';
import { useMountedRef } from '../hooks/useMountedRef.ts';
import type { ConnectionStatus } from '../services/websocket-client.types.ts';
import type { SendMessagePropsT } from '../types/messages.ts';
import type { Attachment } from '../types/files.ts';
import { decorateWithAttachments } from '../files/attachment-marker.ts';
import { messages } from '../localization/messages.ts';
import { buildTurnIndex, selectedTurnIndex, visibleRailTurnIndex } from './turn-index.ts';
import { processPageFor } from './process/index.ts';
import { resolveLoadingLine } from './loading-line.ts';
import { resolveErrorNotice } from './error-notice.ts';
import {
  isStageRunActive,
  presentationTargetForStage,
  resolveStageView,
  voiceScreenSelectionForStage,
  type StageView,
} from './stage-view.ts';
import { surfaceRendersSomething } from './surface-content.ts';
import { formatNarrationLine } from './narration-line.ts';
import { resolveDockNarration } from './dock-narration.ts';
import { resolveActivityContext } from './activity-context.ts';
import { useComposerDraftState } from './ComposerDraftContext.tsx';
import { composerAction, composerMessage, type ComposerItem } from './composer-draft.ts';
import { useTurnHistory } from './useTurnHistory.ts';

export interface StageConnection {
  isReady: boolean;
  connectionStatus: ConnectionStatus;
  initError: boolean;
}

/** Turn selection, optimistic requests, and composer state shared by stage entries. */
export function useStageController({
  connection,
  canSubmit,
  submitMessage,
}: {
  connection: StageConnection;
  canSubmit: boolean;
  submitMessage: (request: SendMessagePropsT) => Promise<void>;
}) {
  const intl = useIntl();
  const store = useMessagingStore();
  const mounted = useMountedRef();
  const composerDraft = useComposerDraftState();
  const [selectedTurnId, setSelectedTurnId] = useState<string | null>(null);
  const [activityOpen, setActivityOpen] = useState(false);
  const [pendingRequest, setPendingRequest] = useState<string | null>(null);
  const [dismissedErrorRunId, setDismissedErrorRunId] = useState<string | null>(null);
  const turns = useMemo(() => buildTurnIndex(store.contents), [store.contents]);
  const followAfterTurnId = useRef<string | undefined>(undefined);
  const selectedIndex = selectedTurnIndex(turns, selectedTurnId);
  const setSelectedIndex = useCallback(
    (index: number | null) => {
      followAfterTurnId.current = undefined;
      setSelectedTurnId(index === null ? null : (turns[index]?.id ?? null));
    },
    [turns],
  );

  const prevTurnCount = useRef(turns.length);
  useEffect(() => {
    if (turns.length > prevTurnCount.current) {
      setPendingRequest(null);
    }
    prevTurnCount.current = turns.length;
  }, [turns.length]);
  const requestPending = store.userRequestPending;
  useEffect(() => {
    if (!requestPending) {
      setPendingRequest(null);
    }
  }, [requestPending]);

  const surfaceEntries = [...store.a2uiSurfaces.surfaces.entries()];
  const liveHeadIndex = turns.length - 1;
  const resolvedIndex = selectedIndex !== null ? selectedIndex : liveHeadIndex;
  const resolvedTurn = resolvedIndex >= 0 ? turns[resolvedIndex] : undefined;
  const atLiveHead = selectedIndex === null || selectedIndex === liveHeadIndex;
  const activePart = store.activeProcessPart;
  const processPartKey =
    activePart && processPageFor(activePart.componentName)
      ? (activePart.streaming?.toolCallId ?? activePart.messageId ?? 'live')
      : null;
  const prevLive = useRef<StageView | null>(null);
  const view = resolveStageView(
    {
      turns,
      selectedIndex,
      followAfterTurnId: followAfterTurnId.current,
      surfaces: surfaceEntries.map(([id, surface]) => ({
        id,
        responseId: surface.responseId,
        lastTouchedResponseId: surface.lastTouchedResponseId,
        awaitingReplacement: surface.awaitingReplacement,
        hasContent: surfaceRendersSomething(surface),
      })),
      processPartKey,
      userRequestPending: store.userRequestPending,
      voiceRunActive: store.speechEnabled && store.voiceRunBusy,
    },
    prevLive.current,
  );
  if (atLiveHead) {
    prevLive.current = view;
  }

  const viewingHistory = !atLiveHead && resolvedTurn !== undefined;
  let shownTurnIndex = resolvedIndex;
  if (view.kind === 'surface' && view.contentId) {
    const heldIndex = turns.findIndex((turn) => turn.surfaceContentId === view.contentId);
    if (heldIndex >= 0) {
      shownTurnIndex = heldIndex;
    }
  }
  const currentRailIndex = visibleRailTurnIndex(turns, shownTurnIndex);
  const clearRestore = useTurnHistory({
    turnCount: turns.length,
    shownIndex: currentRailIndex,
    onSelect: setSelectedIndex,
  });
  const selectTurn = (index: number) => {
    clearRestore();
    setSelectedIndex(index);
  };
  const followFromCurrentView = (acceptedResponseId?: string) => {
    setSelectedIndex(null);
    prevLive.current = view;
    const echoedIndex = acceptedResponseId
      ? turns.findIndex((turn) => turn.responseId === acceptedResponseId)
      : -1;
    const originIndex = echoedIndex >= 0 ? echoedIndex - 1 : turns.length - 1;
    followAfterTurnId.current = turns[originIndex]?.id;
  };
  const followVoiceRequest = useRef(followFromCurrentView);
  followVoiceRequest.current = followFromCurrentView;
  useEffect(
    () =>
      reaction(
        () => store.voiceAcceptedResponseId,
        (responseId) => {
          if (!responseId || !store.speechEnabled) {
            return;
          }
          clearRestore();
          followVoiceRequest.current(responseId);
        },
      ),
    [store, clearRestore],
  );

  const narrationLine = formatNarrationLine(store.narration.line, intl);
  const loadingLine =
    view.kind === 'loading'
      ? resolveLoadingLine(
          {
            ...connection,
            runInFlight: store.userRequestPending,
            narrationLine,
            settledEmpty:
              !store.userRequestPending &&
              connection.isReady &&
              !store.speechEnabled &&
              !store.voiceRunBusy,
            terminalText: store.lastRunError?.message ?? null,
          },
          {
            couldNotConnect: intl.formatMessage(messages.couldNotConnect),
            connecting: intl.formatMessage(messages.connecting),
            gettingReady: intl.formatMessage(messages.gettingReady),
            thinking: intl.formatMessage(messages.thinking),
            pageLoadFailed: intl.formatMessage(messages.pageLoadFailed),
          },
        )
      : null;
  const liveTurn = turns[liveHeadIndex];
  const errorNotice = resolveErrorNotice({
    viewKind: view.kind,
    lastRunError: store.lastRunError,
    liveTurn: liveTurn
      ? { responseId: liveTurn.responseId, responseText: liveTurn.responseText }
      : undefined,
  });
  const voiceScreenSelection = voiceScreenSelectionForStage({
    view,
    turns,
    processNarration: narrationLine,
    copy: {
      transcriptOpen: intl.formatMessage(messages.transcriptOpen),
      workInProgress: intl.formatMessage(messages.workInProgress),
      screenTextTruncated: intl.formatMessage(messages.screenTextTruncated),
    },
  });
  // A complete screen can precede terminal run status. Only the latter clears working controls.
  const runInFlight = isStageRunActive(store.userRequestPending);
  const voiceActive = store.speechEnabled;
  const dockNarration = resolveDockNarration(
    {
      voiceActive,
      voiceState: store.voiceState,
      voiceCaption: store.voiceCaption,
      voiceRunBusy: store.voiceRunBusy,
      runInFlight,
      narration: narrationLine,
    },
    intl,
  );

  const sendMessage = (
    text: string,
    files?: Attachment[],
    selections: ComposerItem[] = [],
    onAdmitted?: SendMessagePropsT['onAdmitted'],
    onError?: (error: Error) => void,
  ) => {
    if (!canSubmit) {
      return;
    }
    const hasFiles = !!files && files.length > 0;
    const requestText = composerMessage(text, selections);
    const decoratedText = hasFiles
      ? decorateWithAttachments(
          requestText,
          files.map((file) => file.name),
        )
      : requestText;
    if (hasFiles) {
      store.rememberSentAttachments(decoratedText, files);
    }
    clearRestore();
    followFromCurrentView();
    setPendingRequest(decoratedText);
    composerDraft.consume(selections);
    void submitMessage({
      instruction: decoratedText,
      presentationTarget: presentationTargetForStage(view),
      ...(selections.length > 0
        ? { metadata: { channel: 'omnibox', a2uiAction: composerAction(selections) } }
        : {}),
      ...(hasFiles ? { files } : {}),
      onAdmitted,
    }).catch((error: unknown) => {
      if (mounted.current) {
        composerDraft.restore(selections);
      }
      console.error('[StageController] message submission failed', error);
      onError?.(error instanceof Error ? error : new Error('Message failed'));
    });
  };
  const sendAudio = (audio: string) => {
    const presentationTarget = presentationTargetForStage(view);
    followFromCurrentView();
    void submitMessage({ audio, presentationTarget });
  };
  const activityContext = resolveActivityContext({
    turns,
    selectedTurn: resolvedTurn,
    viewingHistory,
    currentResponseId: store.narration.responseId,
    pendingRequest,
  });
  const activityStatus =
    loadingLine && ['connecting', 'preparing', 'working'].includes(loadingLine.kind)
      ? loadingLine.text
      : dockNarration.text;
  return {
    view,
    turns,
    surfaceEntries,
    loadingLine,
    errorNotice,
    currentRailIndex,
    viewingHistory,
    activityContext,
    activityStatus,
    activityOpen,
    setActivityOpen,
    selectTurn,
    setSelectedIndex,
    dismissedErrorRunId,
    setDismissedErrorRunId,
    pendingRequest,
    runInFlight,
    voiceActive,
    voiceScreenSelection,
    dockNarration,
    composerDraft,
    sendMessage,
    sendAudio,
    followFromCurrentView,
    ready: connection.isReady && connection.connectionStatus === 'connected',
  };
}
export type StageController = ReturnType<typeof useStageController>;
